// Lista de propostas CRM (tabular) — /crm/proposals (Story 11.6)
// - useInfiniteQuery com filtros (status/responsável/"a vencer em N dias")
// - Paginação prev/next por cursor (DR-032), reuso de <Pagination>
// - Skeleton no loading inicial (AC10), empty/error states
// - Badge de status colorido por categoria (AC10)
// - Coluna "Validade" com destaque quando vence em <= 3 dias ou já venceu (AC10)
// - Botão "Criar proposta" (admin/comercial)

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, RefreshCw, Search } from 'lucide-react';

import type { CrmProposalStatusSchema } from '@control-center/shared';
import { formatBR } from '@control-center/shared';

import { EmptyState as RichEmptyState } from '../../../components/common/EmptyState';
import { Pagination } from '../../../components/common/Pagination';
import { PillButton } from '../../../components/clients/PillButton';
import { Select } from '../../../components/clients/form-primitives';
import {
  PROPOSAL_STATUS_BADGE,
  PROPOSAL_STATUS_LABELS,
  formatProposalMoney,
  validityClass,
  validityFlag,
} from '../../../lib/proposal-helpers';
import {
  useCrmProposalsInfinite,
  type CrmProposalsListFilters,
} from '../../../hooks/use-crm-proposals';
import { useUsers } from '../../../hooks/use-users';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { extractErrorMessage } from '../../../lib/errors';
import type { CrmProposalListItem } from '../../../lib/crm-proposals-api';

const STATUS_FILTERS: { value: '' | CrmProposalStatusSchema; label: string }[] = [
  { value: '', label: 'Todos os status' },
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'enviada', label: 'Enviada' },
  { value: 'visualizada', label: 'Visualizada' },
  { value: 'aceita', label: 'Aceita' },
  { value: 'recusada', label: 'Recusada' },
  { value: 'expirada', label: 'Expirada' },
  { value: 'convertida', label: 'Convertida' },
];

const EXPIRING_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Qualquer validade' },
  { value: '3', label: 'A vencer em 3 dias' },
  { value: '7', label: 'A vencer em 7 dias' },
  { value: '15', label: 'A vencer em 15 dias' },
];

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

export function CrmProposalsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { canWrite } = useCrmPermissions();

  const { data: users } = useUsers();

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | CrmProposalStatusSchema>('');
  const [expiringFilter, setExpiringFilter] = useState('');
  const [responsibleFilter, setResponsibleFilter] = useState('');

  const debouncedQuery = useDebouncedValue(query, 300);

  const filters: CrmProposalsListFilters = useMemo(() => {
    const f: CrmProposalsListFilters = {};
    if (statusFilter) f.status = statusFilter;
    if (expiringFilter) f.expiring_days = Number(expiringFilter);
    if (responsibleFilter) f.responsible_user_id = responsibleFilter;
    return f;
  }, [statusFilter, expiringFilter, responsibleFilter]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCrmProposalsInfinite(filters);

  const [pageIndex, setPageIndex] = useState(0);
  useEffect(() => setPageIndex(0), [filters]);

  const pages = useMemo(() => data?.pages ?? [], [data]);
  const safeIndex = Math.min(pageIndex, Math.max(pages.length - 1, 0));
  const currentPage = pages[safeIndex];
  const allProposals = useMemo(() => currentPage?.items ?? [], [currentPage]);

  // Busca textual (número/cliente/lead) é client-side (mantém backend simples).
  const proposals = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return allProposals;
    return allProposals.filter((p) => {
      const haystack = [
        p.proposal_number,
        p.client?.name ?? '',
        p.lead?.name ?? '',
        p.deal?.title ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allProposals, debouncedQuery]);

  const total = currentPage?.meta.total ?? null;

  const rangeStart = useMemo(
    () =>
      allProposals.length === 0
        ? 0
        : pages.slice(0, safeIndex).reduce((acc, p) => acc + p.items.length, 0) + 1,
    [pages, safeIndex, allProposals.length],
  );
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + allProposals.length - 1;

  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < pages.length - 1 || Boolean(hasNextPage);

  const goNext = (): void => {
    if (safeIndex < pages.length - 1) {
      setPageIndex(safeIndex + 1);
      return;
    }
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage().then((res) => {
        if (!res.isError) setPageIndex(safeIndex + 1);
      });
    }
  };

  const hasActiveFilters =
    !!debouncedQuery || !!statusFilter || !!expiringFilter || !!responsibleFilter;

  const clearFilters = (): void => {
    setQuery('');
    setStatusFilter('');
    setExpiringFilter('');
    setResponsibleFilter('');
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="m-0 text-[30px] font-bold leading-none tracking-[-0.025em] text-ink">
            Propostas
          </h1>
          <p className="mt-[6px] max-w-[560px] text-[13px] leading-[1.5] text-muted">
            Cada proposta tem um número rastreável e um ciclo do rascunho até a conversão em
            assinatura.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PillButton
            onClick={() => refetch()}
            icon={
              <RefreshCw
                size={14}
                strokeWidth={2}
                className={isFetching ? 'animate-spin' : undefined}
              />
            }
          >
            Atualizar
          </PillButton>
          {canWrite && (
            <PillButton
              solid
              onClick={() => navigate('/crm/proposals/new')}
              icon={<Plus size={14} strokeWidth={2} />}
            >
              Criar proposta
            </PillButton>
          )}
        </div>
      </header>

      {/* Filtros */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_180px_180px_180px]">
        <div className="relative">
          <Search
            size={14}
            strokeWidth={2}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por número, cliente, lead…"
            aria-label="Buscar propostas"
            className="h-10 w-full rounded-[10px] border border-line bg-panel pl-9 pr-3 text-[13px] text-ink placeholder:text-muted-3 outline-none transition-colors focus:border-line-2"
          />
        </div>
        <Select
          aria-label="Filtrar por status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | CrmProposalStatusSchema)}
        >
          {STATUS_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filtrar por validade"
          value={expiringFilter}
          onChange={(e) => setExpiringFilter(e.target.value)}
        >
          {EXPIRING_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filtrar por responsável"
          value={responsibleFilter}
          onChange={(e) => setResponsibleFilter(e.target.value)}
        >
          <option value="">Todos os responsáveis</option>
          {(users ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
      </div>

      {/* Conteúdo */}
      {isLoading ? (
        <ProposalTableSkeleton />
      ) : isError ? (
        <ErrorState
          message={extractErrorMessage(error, 'Não conseguimos carregar as propostas.')}
          onRetry={() => refetch()}
        />
      ) : proposals.length === 0 ? (
        hasActiveFilters ? (
          <FilteredEmpty onClear={clearFilters} />
        ) : (
          <RichEmptyState
            icon={FileText}
            title="Sem propostas ainda"
            description="Crie a primeira proposta pra registrar valores, validade e desconto — e acompanhar o ciclo até a conversão."
            bullets={[
              'Número sequencial automático (PROP-ano-seq)',
              'Ciclo de status: rascunho → enviada → aceita → convertida',
              'Pré-preenchimento a partir de um deal',
            ]}
            primaryAction={
              canWrite
                ? {
                    label: 'Criar primeira proposta',
                    icon: <Plus size={14} strokeWidth={2} />,
                    onClick: () => navigate('/crm/proposals/new'),
                  }
                : undefined
            }
          />
        )
      ) : (
        <>
          <ProposalTable proposals={proposals} onOpen={(p) => navigate(`/crm/proposals/${p.id}`)} />
          <Pagination
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPrev={() => setPageIndex(Math.max(safeIndex - 1, 0))}
            onNext={goNext}
            isLoading={isFetchingNextPage}
            noun="proposta"
          />
        </>
      )}
    </div>
  );
}

// =============================================================================
// Tabela
// =============================================================================

interface ProposalTableProps {
  proposals: CrmProposalListItem[];
  onOpen: (proposal: CrmProposalListItem) => void;
}

/** Nome do destinatário (cliente > lead > deal) pra exibir na linha. */
function recipientLabel(p: CrmProposalListItem): string {
  return p.client?.name ?? p.lead?.name ?? p.deal?.title ?? '—';
}

function ProposalTable({ proposals, onOpen }: ProposalTableProps): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider text-muted-3">
            <th className="px-4 py-3 font-medium">Número</th>
            <th className="px-4 py-3 font-medium">Destinatário</th>
            <th className="px-4 py-3 font-medium">Produto</th>
            <th className="px-4 py-3 font-medium">Mensal</th>
            <th className="px-4 py-3 font-medium">Validade</th>
            <th className="px-4 py-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {proposals.map((p) => {
            const flag = validityFlag(p.valid_until);
            return (
              <tr
                key={p.id}
                onClick={() => onOpen(p)}
                className="cursor-pointer border-b border-line/60 transition-colors last:border-0 hover:bg-panel-2"
              >
                <td className="px-4 py-3 font-medium tabular-nums text-ink">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(p);
                    }}
                    className="text-left transition-colors hover:text-link"
                  >
                    {p.proposal_number}
                  </button>
                </td>
                <td className="px-4 py-3 text-muted-2">{recipientLabel(p)}</td>
                <td className="px-4 py-3 text-muted-2">{p.saas_product?.name ?? '—'}</td>
                <td className="px-4 py-3 tabular-nums text-muted">
                  {formatProposalMoney(p.monthly_value)}
                </td>
                <td className={`px-4 py-3 tabular-nums ${validityClass(flag)}`}>
                  {formatBR(p.valid_until)}
                  {flag === 'expired' && ' (vencida)'}
                  {flag === 'soon' && ' (a vencer)'}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[11px] ${PROPOSAL_STATUS_BADGE[p.status]}`}
                  >
                    {PROPOSAL_STATUS_LABELS[p.status]}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProposalTableSkeleton(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <div className="flex flex-col divide-y divide-line/60">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-[14px]">
            <div className="h-3 w-32 animate-pulse rounded bg-panel-2" />
            <div className="h-3 w-40 animate-pulse rounded bg-panel-2" />
            <div className="ml-auto h-3 w-16 animate-pulse rounded bg-panel-2" />
          </div>
        ))}
      </div>
    </div>
  );
}

function FilteredEmpty({ onClear }: { onClear: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-line bg-panel py-12 text-center">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Nenhuma proposta bate com esses filtros.
      </h2>
      <p className="mx-auto mt-1 max-w-[360px] text-[12px] text-muted">
        Tente afrouxar a busca ou limpar os filtros.
      </p>
      <div className="mt-4 flex justify-center">
        <PillButton onClick={onClear}>Limpar filtros</PillButton>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/40 bg-panel p-6">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Não conseguimos carregar as propostas.
      </h2>
      <p className="mt-1 text-[12px] text-muted">{message}</p>
      <div className="mt-4">
        <PillButton onClick={onRetry} icon={<RefreshCw size={14} strokeWidth={2} />}>
          Tentar de novo
        </PillButton>
      </div>
    </div>
  );
}
